/* eslint-disable react/jsx-props-no-spreading */
import Slider from 'react-slick';
import {Button} from 'letrus-ui';
import useRecommendationBanner from 'containers/Dashboard/CTABanners/RecommendationBanner';
import useStreakBanner from 'containers/Dashboard/CTABanners/StreakBanner';
import {IconProp} from '@fortawesome/fontawesome-svg-core';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import useWriteTipsBanner from 'containers/Dashboard/CTABanners/WriteTipsBanner';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {useNavigate} from 'react-router-dom';
import styles from './CTABannersCarousel.module.scss';
import useC5Banner from '../C5Banner';
import useActivitiesToFinishBanner from '../ActivitiesToFinishBanner';
import useSocioEconomicalBanner from '../SocioEconomicalBanner';
import useOlympicsBanner from '../ContestsBanner';

export interface CTABanner {
  priority: number;
  title: string;
  icon?: IconProp;
  description: string;
  theme: 'light' | 'dark';
  color?: string;
  action?: {
    text: string;
    onClick: () => void;
  };
  isVisible: () => boolean;
  modals?: React.ReactNode;
}

function CTABannersCarousel({
  shouldShowContestResult = false,
}: {
  shouldShowContestResult?: boolean;
}) {
  const navigate = useNavigate();
  const {trackEvent} = useTrackEvent();
  const contestsBanners = useOlympicsBanner({priority: 1});
  const writeTipsBanner = useWriteTipsBanner({priority: 2});
  const activitiesToFinishBanner = useActivitiesToFinishBanner({priority: 3});
  const recommendationBanner = useRecommendationBanner({priority: 4});
  const streakBanner = useStreakBanner({priority: 5});
  const c5Banner = useC5Banner({priority: 6});
  const socioEconomicalBanner = useSocioEconomicalBanner({priority: 7});

  function handleContestResultClick() {
    trackEvent('accessed_contest_result_page_banner_student');

    navigate('/resultado-olimpiadas');
  }

  const resultBanner: CTABanner = {
    priority: 0,
    title: 'Olimpíada Linguagem em Ação',
    description:
      'Seu resultado já está disponível! Confira sua nota e descubra se tem algum reconhecimento te esperando.',
    theme: 'light',
    color: '#7518f7',
    icon: ['fad', 'medal'] as IconProp,
    isVisible: () => shouldShowContestResult,
    action: {
      text: 'Ver resultado',
      onClick: handleContestResultClick,
    },
  };

  const banners: CTABanner[] = [
    resultBanner,
    c5Banner,
    streakBanner,
    writeTipsBanner,
    recommendationBanner,
    socioEconomicalBanner,
    activitiesToFinishBanner,
    ...(contestsBanners ?? []),
  ];

  const visibleBanners = banners
    .filter((banner) => banner.isVisible())
    .sort((a, b) => a.priority - b.priority);

  return (
    <>
      {!!visibleBanners.length && (
        <Slider
          dots
          infinite
          autoplay
          autoplaySpeed={5 * 1000}
          className={styles.slider}
        >
          {visibleBanners.map((banner) => (
            <div key={banner.priority}>
              <div
                className={`${styles.banner} ${styles[banner.theme]}`}
                style={{
                  background: `linear-gradient(to right, ${banner.color}50, ${banner.color}20)`,
                }}
              >
                <div className={styles.title}>
                  {banner.icon && (
                    <FontAwesomeIcon
                      icon={banner.icon}
                      className={styles.titleIcon}
                      color={`color-mix(in srgb, ${banner.color} 90%, black)`}
                      style={{
                        backgroundColor: `color-mix(in srgb, ${banner.color} 15%, white)`,
                      }}
                    />
                  )}

                  <div>
                    <h2>{banner.title}</h2>
                    <span>{banner.description}</span>
                  </div>
                </div>

                {!!banner.action && (
                  <Button variant="secondary" onClick={banner.action.onClick}>
                    {banner.action.text}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Slider>
      )}

      {visibleBanners.map((banner) => banner.modals)}
    </>
  );
}

export default CTABannersCarousel;
